// frontend/src/pages/ModeratorPanel.js
import React, { useEffect, useState } from 'react';
import VideoPlayer from '../components/VideoPlayer';
import MultiAnnotationForm from '../components/MultiAnnotationForm';
import api from '../services/api';
import '../App.css';

function ModeratorPanel() {
  const [pendingVideos, setPendingVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const POLLING_INTERVAL = 5000; // Poll every 5 seconds

  const fetchPendingVideos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/videos');
      const pending = response.data.filter(
        (video) => video.status === 'Pending' || video.status === 'Annotated'
      );
      setPendingVideos(pending);
    } catch (error) {
      console.error('Error fetching pending videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingVideos();
    const interval = setInterval(fetchPendingVideos, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handleAnnotationsSubmitted = (updatedVideo) => {
    console.log('Annotations submitted:', updatedVideo);
    fetchPendingVideos();
  };

  const processVideo = async (videoId) => {
    setProcessing(true);
    try {
      await api.post(`/videos/${videoId}/process`);
      setProcessing(false);
      setSelectedVideo(null);
      fetchPendingVideos();
    } catch (error) {
      console.error('Error processing video:', error);
      setProcessing(false);
    }
  };

  // Function to get status class for styling
  const getStatusClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'status-pending';
      case 'Annotated':
        return 'status-annotated';
      default:
        return '';
    }
  };

  return (
    <div className="moderator-container">
      <div className="container">
        <h2 className="section-header">Content Moderation Panel</h2>
        
        {loading && pendingVideos.length === 0 ? (
          <div className="loading-indicator">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading videos...</p>
          </div>
        ) : pendingVideos.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-check-circle"></i>
            <p>No pending videos at the moment. All content has been moderated.</p>
          </div>
        ) : (
          <div className="pending-videos">
            <h3>Pending Content</h3>
            <div className="video-grid">
              {pendingVideos.map((video) => (
                <div key={video._id} className="video-card">
                  <div className="video-info">
                    <h3>{video.title}</h3>
                    <span className={`video-status ${getStatusClass(video.status)}`}>
                      {video.status}
                    </span>
                  </div>
                  <div className="video-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleVideoSelect(video)}
                    >
                      <i className="fas fa-edit"></i> Moderate
                    </button>
                    {video.status === 'Annotated' && (
                      <button 
                        className="btn btn-success"
                        onClick={() => processVideo(video._id)}
                        disabled={processing}
                      >
                        {processing ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i> Processing...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cogs"></i> Process Video
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedVideo && (
        <div className="container moderation-panel">
          <div className="video-section">
            <h3 className="section-header">{selectedVideo.title}</h3>
            <VideoPlayer
              videoSrc={selectedVideo.fileUrl}
              onTimeUpdate={() => {}}
            />
          </div>
          <div className="annotation-section">
            <h3 className="section-header">Add Content Warnings</h3>
            <MultiAnnotationForm
              videoId={selectedVideo._id}
              onAnnotationsSubmitted={handleAnnotationsSubmitted}
            />
            {processing && (
              <div className="processing-indicator">
                <div className="progress-container">
                  <div className="progress-indeterminate"></div>
                </div>
                <p>Processing video, please wait...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ModeratorPanel;
